import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import "../ProcessAndProcedure/MedicalReports/MedicalReports.scss";
import Loading from "../../utils/Loader/Loading";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { calculateUserMedicalStatus } from "../../components/Users/calculateUserMedicalStatus";
import useIsOneIntroducer from "../../hooks/useIsOneIntroducer";

const CollectionReportPrint = () => {
  const navigate = useNavigate();
  const [receivedAmounts, setReceivedAmounts] = useState({});
  const [initialRowSelection, setInitialRowSelection] = useState([]);
  const [rowSelection, setRowSelection] = useState(initialRowSelection);
  const [loading, setLoading] = useState(true);
  const pdfRef = useRef();
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);
  const queryData = Object.fromEntries(queryParams.entries());
  const filterData = queryData?.toDate || {};
  const [dues, setDues] = useState([]);
  const [received, setReceived] = useState([]);
  const [commission, setCommission] = useState([]);
  const [paid, setPaid] = useState([]);
  const [netTotal, setNetTotal] = useState([]);

  const [usersData, setUsersData] = useState([]);

  const methodFilterPatientsDetails = async (filterData) => {
    try {
      setLoading(true);
      const query = new URLSearchParams(filterData).toString();
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v2/users/search-patients-for-collection?${query}`,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${localStorage?.getItem("jwtToken")}`,
          },
        }
      );
      const data = response?.data?.data;
      console.log("data", data);
      if (response?.data?.status === "OK") {
        setUsersData(data || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePDF = async () => {
    if (!pdfRef.current) {
      console.error("PDF content is not available");
      return;
    }

    const htmlContent = pdfRef.current.outerHTML;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/user-details/generate-pdf/collection-report`,
        { htmlContent, filterData },
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Open the PDF in a new tab
      window.open(pdfUrl, "_blank");

      // Clean up URL object after use
      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error("Error generating PDF", error);
    }
  };

  // total calculations

  const totalReceived = usersData?.reduce(
    (acc, user) =>
      acc + parseFloat(user?.userDetail?.user_payment?.paid_amount || 0),
    0
  );
  const totalNetAmount = usersData?.reduce(
    (acc, user) =>
      acc + parseFloat(user?.userDetail?.user_payment?.net_amount || 0),
    0
  );
  const totalCommission = usersData?.reduce(
    (acc, user) =>
      acc + parseFloat(user?.userDetail?.user_payment?.commission || 0),
    0
  );
  const totalDue = usersData?.reduce(
    (acc, user) => acc + parseFloat(user?.userDetail?.user_payment?.due || 0),
    0
  );

  const totalPaid = usersData?.reduce(
    (acc, user) => acc + parseFloat(user?.userDetail?.user_payment?.paid || 0),
    0
  );

  // custom hooks
  const isOneIntroducer = useIsOneIntroducer();

  const columnHelper = createColumnHelper();

  const columns = useMemo(
    () => [
      columnHelper.accessor("SL.", {
        header: () => (
          <div className="d-flex justify-content-center">
            <span className="text-center px-2">SL</span>
          </div>
        ),
        cell: (row, index) => {
          const reversedSerialNumber = row?.row?.index + 1;

          return (
            <div className="text-start d-flex justify-content-start fw-bold">
              <span className="text-center">{reversedSerialNumber}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("Date", {
        header: () => (
          <div className="d-flex justify-content-center">
            <span className="text-center px-2">Date</span>
          </div>
        ),
        cell: (row, index) => {
          return (
            <div className="text-start d-flex justify-content-start fw-bold">
              <span className="text-center">
                {format(new Date(row?.row?.original?.createdAt), "dd-MM-yyyy")}
              </span>
            </div>
          );
        },
      }),
      columnHelper.accessor("registration_no", {
        header: () => (
          <span className="d-flex justify-content-center">Reg. No</span>
        ),
        cell: (info, index) => {
          const reversedIndex = usersData.length - index - 1;
          return (
            <div className="d-flex justify-content-start">
              {info?.row?.original?.reg_prefix || ""}
              {info.renderValue(reversedIndex)}
            </div>
          );
        },
      }),
      columnHelper.accessor("f_name", {
        header: () => (
          <span className="d-flex justify-content-center">Passenger</span>
        ),
        cell: (info, index) => {
          const reversedIndex = usersData.length - index - 1;
          return (
            <div className="d-flex justify-content-start">
              {info.renderValue(reversedIndex)}
            </div>
          );
        },
      }),
      columnHelper.accessor("passport_no", {
        header: () => (
          <span className="d-flex justify-content-center">Passport No</span>
        ),
        cell: (info, index) => {
          const reversedIndex = usersData.length - index - 1;
          return (
            <div className="d-flex justify-content-start">
              {info.renderValue(reversedIndex)}
            </div>
          );
        },
      }),
      ...(!isOneIntroducer
        ? [
            columnHelper.accessor("userDetail.introducer_user.f_name", {
              header: () => (
                <span className="d-flex justify-content-center">Agency</span>
              ),
              cell: (info, index) => {
                const value1 =
                  info?.row?.original?.userDetail?.introducer_user?.f_name;
                const representative_Mob =
                  info?.row?.original?.userDetail?.introducer_user?.user_users_maps[0]?.representative_user_map?.mobile?.slice(
                    -2
                  );
                return (
                  <div>
                    <span className="d-flex justify-content-start">
                      {representative_Mob ? (
                        <span className="text-center d-flex justify-content-start">
                          {value1} - ({representative_Mob})
                        </span>
                      ) : (
                        <span className="text-center d-flex justify-content-start">
                          {value1}
                        </span>
                      )}
                    </span>
                  </div>
                );
              },
            }),
          ]
        : []),

      columnHelper.accessor("user", {
        header: () => (
          <span
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            Country
          </span>
        ),
        cell: (info) => {
          const country = info?.row?.original?.userDetail?.country?.name;
          return <span>{country}</span>;
        },
      }),
      columnHelper.accessor("userDetail.package.name", {
        header: () => (
          <span className="d-flex justify-content-center">Test/Ref By</span>
        ),
        cell: (info, index) => {
          const user = info?.row?.original;

          // Check if role_id is 5
          if (user?.role_id === 5) {
            // Get the tests array from the user
            const tests = user?.user_collections || [];
            let investigation31Added = false;

            const investigationNames = tests
              .filter((test) => {
                if (test?.investigation?.id === 31) {
                  if (investigation31Added) {
                    return false;
                  }
                  investigation31Added = true;
                  return true;
                }
                return true;
              })
              .map((test) => test.investigation?.name)
              .filter(Boolean)
              .join(", ");
            return (
              <div className="d-flex justify-content-start">
                {investigationNames || "No Investigations"}
              </div>
            );
          } else {
            // If role_id is not 5, render the default value
            const reversedIndex = usersData.length - index - 1;
            return (
              <div className="d-flex justify-content-start">
                {info.renderValue(reversedIndex)}
              </div>
            );
          }
        },
      }),
      columnHelper.accessor("user", {
        header: () => (
          <span
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            Received
          </span>
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <span
              style={{
                textAlign: "end",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {row?.userDetail?.user_payment?.paid_amount || 0}
            </span>
          );
        },
      }),
      columnHelper.accessor("user", {
        header: () => (
          <span
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            Net Amount
          </span>
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <span
              style={{
                textAlign: "end",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {row?.userDetail?.user_payment?.net_amount || 0}
            </span>
          );
        },
      }),
      columnHelper.accessor("user", {
        header: () => (
          <span
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            Balance
          </span>
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <span
              style={{
                textAlign: "end",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {row?.userDetail?.user_payment?.commission || 0}
            </span>
          );
        },
      }),
      columnHelper.accessor("user", {
        header: () => (
          <span
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            Dues
          </span>
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <span
              style={{
                textAlign: "end",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {row?.userDetail?.user_payment?.due || 0}
            </span>
          );
        },
      }),
      columnHelper.accessor("user", {
        header: () => (
          <span
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            Paid
          </span>
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <span
              style={{
                textAlign: "end",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {row?.userDetail?.user_payment?.paid || 0}
            </span>
          );
        },
      }),
      columnHelper.accessor("user", {
        header: () => (
          <span
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            Remarks
          </span>
        ),
        cell: (info) => {
          return <span className="py-5"> </span>;
        },
      }),
    ],
    [receivedAmounts]
  );

  const table = useReactTable({
    data: usersData,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      rowSelection: rowSelection,
    },
    onRowSelectionChange: (selectedRows) => {
      setRowSelection(selectedRows);
    },
  });

  useEffect(() => {
    if (queryData) {
      methodFilterPatientsDetails(queryData);
    } else {
      methodFilterPatientsDetails({
        fromDate: new Date(),
        toDate: new Date(),
        viewOptions: "all",
      });
    }
  }, []);

  return (
    <>
      <div className="card medical-report-container">
        <div className="card-body">
          <div className="rounded">
            <div className="card shadow-none mb-3">
              <div className="border p-3 rounded d-flex align-items-start justify-content-between">
                <h6
                  className="mb-0 text-uppercase fw-bold"
                  style={{ color: "red" }}
                >
                  Collection Report Print
                </h6>
                <div className="d-flex flex-wrap align-items-center">
                  <div className="d-flex align-items-center gap-3 justify-content-end w-100 mt-0">
                    <button
                      className="btn bg-danger btn-lg px-5 text-white"
                      onClick={handlePDF}
                    >
                      Print
                    </button>
                    <button
                      onClick={() => window.close()}
                      className="btn btn-secondary btn-lg px-5"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <Loading />
            ) : (
              <div ref={pdfRef}>
                <table
                  className="table align-middle table-hover table-bordered mb-0 rounded"
                  style={{ fontSize: "12px" }}
                >
                  <thead className="bg-primary">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header, i) => (
                          <th
                            key={i}
                            id={`th-${i}`}
                            className="text-light text-dark p-1"
                            style={{
                              backgroundColor: "#B2BEB5",
                              color: "black",
                            }}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row, i) => {
                      const {
                        original: { status },
                      } = row;
                      const { isDone } = calculateUserMedicalStatus(
                        row?.original
                      );

                      return (
                        <tr
                          key={i}
                          className={`${
                            isDone && status === 4 && "text-primary"
                          } ${isDone && status === 2 && "text-success"} ${
                            isDone && status === 3 && "text-danger"
                          } ${status === 1 && "text-dark"} 
                 `}
                        >
                          {row.getVisibleCells().map((cell, i) => (
                            <td key={i} className="p-1">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    <tr>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td className="p-1">Total</td>
                      <td className="p-1" style={{ textAlign: "end" }}>
                        {totalReceived}
                      </td>
                      <td className="p-1" style={{ textAlign: "end" }}>
                        {totalNetAmount}
                      </td>
                      <td className="p-1" style={{ textAlign: "end" }}>
                        {totalCommission}
                      </td>
                      <td className="p-1" style={{ textAlign: "end" }}>
                        {totalDue}
                      </td>
                      <td className="p-1" style={{ textAlign: "end" }}>
                        {totalPaid}
                      </td>
                      <td className="p-1" style={{ textAlign: "end" }}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CollectionReportPrint;
